/**
 * Full-screen forge workbench: conversation on the left (the Arche Builder's
 * conversation pane + prompt input, reused), status / screenshots / test
 * builds / production / delete on the right.
 *
 * The prompt box never silently drops a message. On a stopped or failed
 * forge, sending starts the forge and QUEUES the message; it is sent the
 * moment the forge reports running, and handed back to the input if the
 * start fails. A message sent to a forge the platform stopped meanwhile
 * (30 minutes idle, hours used up) is queued the same way once it has
 * finished stopping. A notice above the box always says what state the forge
 * is in, and why it stopped (ADR-023 A21).
 *
 * Compact sends Claude Code's `/compact` as a turn; Clear makes the next
 * message start a new conversation, and folds away the one before it.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Bug,
  Eraser,
  Image as ImageIcon,
  Loader2,
  Play,
  Shrink,
  Trash2,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  PluginConversationPane,
  ActivityStrip,
} from '../../plugin-builder/components/PluginConversationPane';
import { PluginPromptInput } from '../../plugin-builder/components/PluginPromptInput';
import { useForge } from '../hooks/useForge';
import { FORGE_COMPACT_PROMPT, useForgeTurn } from '../hooks/useForgeTurn';
import { ForgeStatusBar } from '../components/ForgeStatusBar';
import { ForgeScreenshotViewer } from '../components/ForgeScreenshotViewer';
import { ForgeNameEditor } from '../components/ForgeNameEditor';
import { ForgeCostBadge } from '../components/ForgeCostBadge';
import { ForgeReportProblemDialog } from '../components/ForgeReportProblemDialog';
import { useForgeCost } from '../hooks/useForgeCost';
import { useForgeDeliveries } from '../hooks/useForgeDeliveries';
import { useForgeArt } from '../hooks/useForgeArt';
import { ForgeArtMedia } from '../components/ForgeArtMedia';
import { ForgeArtApprovalCard } from '../components/ForgeArtApprovalCard';
import { useForgeShowToolActivity } from '../hooks/useForgeShowToolActivity';
import { ForgeToolActivity } from '../components/ForgeToolActivity';
import { ForgeQuestionCard } from '../components/ForgeQuestionCard';
import { ForgePlanCard } from '../components/ForgePlanCard';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ForgeTestBuilds } from '../components/ForgeTestBuilds';
import { ForgePublishPanel } from '../components/ForgePublishPanel';
import { ForgePublishDialog } from '../components/ForgePublishDialog';
import { ForgeArcheSettingsCard } from '../components/ForgeArcheSettingsCard';
import { ForgeArcheSettingsDialog } from '../components/ForgeArcheSettingsDialog';
import { useForgePublishSettings } from '../hooks/useForgePublishSettings';
import { ForgeDeleteDialog } from '../components/ForgeDeleteDialog';
import { forgeDeletedToast, useDeleteForge } from '../hooks/useDeleteForge';
import { FORGE_ARCHE_ID, FORGE_IDLE_STOP_MINUTES } from '../constants';
import type {
  ForgeMessage,
  ForgeStatus,
  ForgeStopReason,
  ForgeSummary,
  ForgeToolEvent,
} from '../types';

/** A message waiting for the forge to be running. `started`: our start request succeeded. */
interface QueuedPrompt {
  text: string;
  started: boolean;
  /**
   * Sent to a forge the platform stopped since the page last looked: start it
   * once it has finished stopping, then send.
   */
  awaitingStop?: boolean;
}

const STARTABLE: ReadonlySet<ForgeStatus> = new Set(['stopped', 'failed']);

/** Typed in the prompt box, these do what the buttons do (as in Claude Code). */
const CLEAR_COMMAND = '/clear';

/** Why a stopped (or stopping) forge is not running, in the owner's words. */
export function forgeStopNotice(status: ForgeStatus, reason: ForgeStopReason | null): string {
  if (status === 'stopping') {
    return reason === 'idle'
      ? `The forge is stopping after ${FORGE_IDLE_STOP_MINUTES} minutes without activity.`
      : 'The forge is stopping.';
  }
  switch (reason) {
    case 'idle':
      return `The forge stopped after ${FORGE_IDLE_STOP_MINUTES} minutes without activity, to save your Forge hours. Your files are saved; sending a message starts it again.`;
    case 'hours':
      return 'The forge stopped: this month’s Forge hours are used up.';
    case 'access':
      return 'The forge stopped: you can no longer publish this arche.';
    default:
      return 'This forge is stopped. Sending a message starts it.';
  }
}

/** Marks where the conversation Claude remembers begins, when the earlier one is shown. */
function newConversationDivider(next: ForgeMessage | undefined): ForgeMessage {
  return {
    role: 'assistant',
    content: '*New conversation from here: Claude doesn’t see the messages above.*',
    timestamp: `${next?.timestamp ?? 'end'}#new-conversation`,
  };
}

function hasPendingClear(forge: ForgeSummary | null): boolean {
  return !!forge?.newConversationRequestedAt;
}

export function ForgePage() {
  const { forgeId } = useParams<{ forgeId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const forgeState = useForge(forgeId ?? null);
  const clearPending = hasPendingClear(forgeState.forge);
  const turn = useForgeTurn(forgeId ?? null, { newConversationPending: clearPending });
  const cost = useForgeCost(forgeId ?? null, turn.isProcessing);
  const deliveries = useForgeDeliveries(forgeId ?? null);
  const art = useForgeArt(forgeId ?? null, { turnRunning: turn.isProcessing });
  const archeSettings = useForgePublishSettings(forgeId ?? null);
  const [showToolActivity, setShowToolActivity] = useForgeShowToolActivity();
  const { deleteForge } = useDeleteForge();
  const [prompt, setPrompt] = useState('');
  const [queued, setQueued] = useState<QueuedPrompt | null>(null);
  // "Publish for testing" on a stopped forge: start it, then build. `started`
  // as for a queued prompt — a start that ends without running drops it.
  const [buildQueued, setBuildQueued] = useState<{ started: boolean } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);

  const { forge } = forgeState;
  const status = forge?.status;
  const isRunning = status === 'running';
  const { sendTurn, markScreenshotsSeen } = turn;

  const openScreenshots = (index: number) => {
    setViewerIndex(index);
    markScreenshotsSeen();
  };

  const handBack = (text: string) => {
    setQueued(null);
    setPrompt(text);
  };

  const startQueued = async (text: string) => {
    try {
      await forgeState.start();
      setQueued((q) => (q ? { ...q, started: true } : q));
    } catch {
      // The refusal (plan, monthly hours, running cap) is in forgeState.error.
      handBack(text);
    }
  };

  const startAndQueue = async (text: string) => {
    setQueued({ text, started: false });
    setPrompt('');
    await startQueued(text);
  };

  const { startNewConversation, cancelNewConversation } = forgeState;
  const clearConversation = () => {
    setShowEarlier(false);
    // A refusal (a turn is open) is in forgeState.error.
    void startNewConversation().catch(() => undefined);
  };

  const sendNow = (text: string) => {
    void sendTurn(text).then((outcome) => {
      // The platform stopped the forge since the page last looked: start it
      // again once it has finished stopping, and send.
      if (outcome === 'not_running') {
        setQueued({ text, started: false, awaitingStop: true });
      }
    });
  };

  const submit = () => {
    const text = prompt.trim();
    if (!text) return;
    if (text === CLEAR_COMMAND) {
      setPrompt('');
      clearConversation();
      return;
    }
    if (isRunning) {
      setPrompt('');
      sendNow(text);
      return;
    }
    if (status && STARTABLE.has(status)) {
      void startAndQueue(text);
      return;
    }
    if (status === 'provisioning') {
      // Already starting (e.g. just created): wait for it.
      setQueued({ text, started: true });
      setPrompt('');
    }
    // stopping / archived: the text stays in the box and the notice says why.
  };

  // Deliver the queued message once the forge runs; hand it back if the
  // start we are waiting on ends without the forge ever running.
  useEffect(() => {
    if (!queued || !status) return;
    if (queued.awaitingStop) {
      if (STARTABLE.has(status)) {
        setQueued({ ...queued, awaitingStop: false });
        void startQueued(queued.text);
      } else if (status === 'provisioning') {
        // Already starting again (another tab): wait for it like our own start.
        setQueued({ ...queued, awaitingStop: false, started: true });
      }
      return;
    }
    if (status === 'running') {
      setQueued(null);
      sendNow(queued.text);
    } else if (queued.started && STARTABLE.has(status)) {
      handBack(queued.text);
    }
  }, [status, queued, sendTurn]);

  const requestTestBuild = forgeState.requestTestBuild;
  const turnBusy = turn.isProcessing;

  // A test build packages the workspace, so it needs a running forge. On a
  // stopped one the button starts it and the build follows — a disabled button
  // whose hint went unread looked like a click that did nothing.
  const publishForTesting = async () => {
    if (isRunning) {
      await requestTestBuild().catch(() => undefined); // refusal is in forgeState.error
      return;
    }
    if (status === 'provisioning') {
      setBuildQueued({ started: true });
      return;
    }
    if (!status || !STARTABLE.has(status)) return;
    setBuildQueued({ started: false });
    try {
      await forgeState.start();
      setBuildQueued((q) => (q ? { started: true } : q));
    } catch {
      setBuildQueued(null); // the refusal is in forgeState.error
    }
  };

  useEffect(() => {
    if (!buildQueued || !status) return;
    if (status === 'running') {
      // A queued prompt may have opened a turn first; build once it finishes.
      if (turnBusy) return;
      setBuildQueued(null);
      void requestTestBuild().catch(() => undefined);
    } else if (buildQueued.started && STARTABLE.has(status)) {
      setBuildQueued(null);
    }
  }, [status, buildQueued, turnBusy, requestTestBuild]);

  const handleDelete = async (id: string) => {
    const outcome = await deleteForge(id);
    toast(forgeDeletedToast(outcome));
    navigate(`/a/${FORGE_ARCHE_ID}`);
  };

  if (forgeState.isLoading || (!forge && !forgeState.error)) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!forge) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">{forgeState.error ?? 'Forge not found'}</p>
        <Button asChild variant="outline">
          <Link to={`/a/${FORGE_ARCHE_ID}`}>Back to forges</Link>
        </Button>
      </div>
    );
  }

  const header = (
    <div className="flex items-center gap-3 border-b px-4 py-2">
      <Button asChild variant="ghost" size="sm">
        <Link to={`/a/${FORGE_ARCHE_ID}`}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Forges
        </Link>
      </Button>
      <ForgeNameEditor forge={forge} onRename={forgeState.rename} />
      <div className="ml-auto flex items-center gap-4">
        <ForgeCostBadge cost={cost} />
        <Button variant="ghost" size="sm" onClick={() => setReportOpen(true)}>
          <Bug className="h-4 w-4 mr-1.5" />
          Report
        </Button>
        <div className="flex items-center gap-2">
          <Switch
            id="forge-show-tool-activity"
            checked={showToolActivity}
            onCheckedChange={setShowToolActivity}
          />
          <Label htmlFor="forge-show-tool-activity" className="text-xs font-normal">
            Tool activity
          </Label>
        </div>
      </div>
    </div>
  );

  const placeholder = isRunning
    ? 'Tell Claude Code what to build or change…'
    : STARTABLE.has(forge.status)
      ? 'Describe what to build. Sending starts the forge.'
      : 'Tell Claude Code what to build or change…';

  const notice = queued ? (
    <div role="status" className="flex items-center gap-2 border-t px-4 py-2 text-sm">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      <span className="flex-1">
        {queued.awaitingStop
          ? 'The forge had stopped. It starts again once it has finished stopping, and your message is sent then.'
          : "Starting the forge. Your message will be sent when it's ready."}
      </span>
      <Button variant="ghost" size="sm" onClick={() => handBack(queued.text)}>
        Cancel
      </Button>
    </div>
  ) : !isRunning ? (
    <div role="status" className="flex items-center gap-2 border-t px-4 py-2 text-sm">
      {forge.status === 'provisioning' || forge.status === 'stopping' ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : null}
      <span className="flex-1 text-muted-foreground" data-testid="forge-state-notice">
        {forge.status === 'provisioning'
          ? 'The forge is starting. This takes a minute or two.'
          : forge.status === 'archived'
            ? 'This forge is archived and can no longer be started.'
            : forge.status === 'failed'
              ? 'The forge stopped with an error. Sending a message starts it again.'
              : forgeStopNotice(forge.status, forge.stopReason ?? null)}
      </span>
      {STARTABLE.has(forge.status) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void forgeState.start().catch(() => undefined)}
          disabled={forgeState.isMutating}
        >
          <Play className="h-4 w-4 mr-1" />
          Start forge
        </Button>
      )}
    </div>
  ) : null;

  // Clear (ADR-023 A21): the conversation before it folds away; Claude does
  // not see it. Shown again on request, below a divider.
  const earlier = turn.earlierMessages;
  const conversationMessages =
    showEarlier && earlier.length > 0
      ? [...earlier, newConversationDivider(turn.messages[0]), ...turn.messages]
      : turn.messages;
  const newConversationBar =
    clearPending || earlier.length > 0 ? (
      <div
        role="status"
        data-testid="forge-new-conversation"
        className="flex items-center gap-2 border-b bg-muted/40 px-4 py-1.5 text-xs"
      >
        <span className="flex-1 text-muted-foreground">
          {clearPending && !turn.isProcessing
            ? 'New conversation: your next message starts fresh. Claude won’t see the earlier messages; your files are unchanged.'
            : 'New conversation: Claude doesn’t see the earlier messages. Your files are unchanged.'}
        </span>
        {earlier.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => setShowEarlier((v) => !v)}
          >
            {showEarlier ? 'Hide earlier messages' : `Show earlier messages (${earlier.length})`}
          </Button>
        )}
        {clearPending && !turn.isProcessing && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => void cancelNewConversation().catch(() => undefined)}
            disabled={forgeState.isMutating}
          >
            Undo
          </Button>
        )}
      </div>
    ) : null;

  const canCompact = isRunning && !turn.isProcessing && !clearPending && turn.messages.length > 0;
  const canClear =
    !turn.isProcessing && !clearPending && turn.messages.length > 0 && forge.status !== 'archived';

  return (
    <div className="h-screen flex flex-col">
      {header}
      {forgeState.error && (
        <div role="alert" className="border-b bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {forgeState.error}
        </div>
      )}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 flex flex-col min-w-0 border-r">
          <PluginConversationPane
            messages={conversationMessages}
            headerContent={newConversationBar}
            isProcessing={turn.isProcessing}
            streamingText={turn.streamingText}
            lastFailure={turn.lastFailure}
            onRetry={() => void turn.retry()}
            // The pane gates tool events on this flag (the builder's admin
            // verbose mode). In Forge it is the user's own "Tool activity"
            // switch, off by default, and the events render compactly.
            isAdmin={showToolActivity}
            renderToolEvents={(events, { isPending }) => (
              <ForgeToolActivity events={events as ForgeToolEvent[]} live={isPending} />
            )}
            thinkingLabel={
              turn.question
                ? 'Waiting for your answer…'
                : turn.plan
                  ? 'Waiting for you to review the plan…'
                  : 'Claude Code is working…'
            }
            pendingVerboseEvents={turn.pendingToolEvents}
            verboseHistory={turn.toolEventHistory}
            emptyStateContent={
              <div className="flex h-full items-center justify-center px-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {earlier.length > 0 || clearPending
                    ? 'Tell Claude Code what to do next. It starts a new conversation; the files from before are still in the workspace.'
                    : isRunning || forge.status === 'provisioning'
                      ? 'Describe the arche you want. Claude Code builds it in this forge and sends screenshots as it goes.'
                      : STARTABLE.has(forge.status)
                        ? 'Describe the arche you want. Sending your first message starts the forge.'
                        : 'This forge is not running.'}
                </p>
              </div>
            }
            footerContent={
              <>
                {turn.question && (
                  <ForgeQuestionCard
                    key={turn.question.questionId}
                    question={turn.question}
                    onAnswer={turn.answerQuestion}
                  />
                )}
                {turn.plan && (
                  <ForgePlanCard
                    key={turn.plan.questionId}
                    plan={turn.plan}
                    onApprove={turn.approvePlan}
                    onRevise={turn.revisePlan}
                  />
                )}
                {turn.artBatch && (
                  <ForgeArtApprovalCard
                    key={turn.artBatch.questionId}
                    pending={turn.artBatch}
                    onDecide={turn.decideArt}
                  />
                )}
                {turn.activity && turn.isProcessing && <ActivityStrip label={turn.activity} />}
                {turn.unseenScreenshots > 0 && viewerIndex === null && (
                  <div
                    role="status"
                    data-testid="forge-new-screenshots"
                    className="flex items-center justify-between gap-2 border-t bg-primary/5 px-4 py-1.5"
                  >
                    <span className="flex items-center gap-2 text-xs">
                      <ImageIcon className="h-3.5 w-3.5" />
                      {turn.unseenScreenshots === 1
                        ? 'There is a new screenshot to view'
                        : `There are ${turn.unseenScreenshots} new screenshots to view`}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs"
                      onClick={() =>
                        openScreenshots(turn.screenshots.length - turn.unseenScreenshots)
                      }
                    >
                      View
                    </Button>
                  </div>
                )}
                {notice}
                <div className="flex items-center justify-end gap-1 border-t px-4 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => sendNow(FORGE_COMPACT_PROMPT)}
                    disabled={!canCompact}
                    title={
                      isRunning
                        ? 'Summarize the conversation so far. Claude keeps the gist and each message costs less.'
                        : 'Start the forge to compact its conversation.'
                    }
                  >
                    <Shrink className="h-3.5 w-3.5 mr-1" />
                    Compact
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={clearConversation}
                    disabled={!canClear || forgeState.isMutating}
                    title="Start a new conversation. Claude forgets the earlier messages; your files stay."
                  >
                    <Eraser className="h-3.5 w-3.5 mr-1" />
                    Clear
                  </Button>
                </div>
                <PluginPromptInput
                  value={prompt}
                  onChange={setPrompt}
                  onSubmit={submit}
                  onStopGeneration={() => void turn.stopTurn()}
                  isProcessing={turn.isProcessing}
                  hasMessages={turn.messages.length > 0}
                  placeholder={placeholder}
                />
              </>
            }
          />
        </div>
        <aside className="w-80 xl:w-96 flex-shrink-0 overflow-y-auto p-3 space-y-3">
          <ForgeStatusBar
            forge={forge}
            onStart={() => void forgeState.start()}
            onStop={() => void forgeState.stop()}
            // A refusal (the Pro gate, a running forge) is an expected outcome for
            // these two controls and is already surfaced through `forgeState.error`.
            onSetModelTier={(tier) => forgeState.setModelTier(tier).catch(() => undefined)}
            onSetSize={(size) => forgeState.setSize(size).catch(() => undefined)}
            isMutating={forgeState.isMutating}
          />
          <ForgeArtMedia
            forgeId={forge.id}
            archeId={forge.archeId}
            art={{
              art: art.art,
              onSetThreshold: art.setThreshold,
              isSavingThreshold: art.isSavingThreshold,
              ...(isRunning && !turn.isProcessing
                ? {
                    onRemake: (name: string) =>
                      sendNow(
                        `Make the art asset "${name}" again: run make_art with remake ["${name}"].`,
                      ),
                  }
                : {
                    remakeHint: isRunning
                      ? 'You can ask for a picture again when the current turn finishes.'
                      : 'Start the forge to make a picture again.',
                  }),
            }}
            screenshots={{
              screenshots: turn.screenshots,
              unseen: turn.unseenScreenshots,
              onOpen: openScreenshots,
              onClear: turn.clearScreenshots,
            }}
            sendFiles={{
              deliveries: deliveries.deliveries,
              onSend: deliveries.send,
              isSending: deliveries.isSending,
              isRunning,
            }}
            {...(isRunning && !turn.isProcessing ? { onAskClaude: sendNow } : {})}
          />
          <ForgeTestBuilds
            archeId={forge.archeId}
            builds={forgeState.testBuilds}
            readyBuild={forgeState.readyTestBuild}
            onBuild={() => void publishForTesting()}
            canBuild={
              (isRunning && !turn.isProcessing) ||
              (!!status && (STARTABLE.has(status) || status === 'provisioning'))
            }
            isBuildRequested={forgeState.isBuildRequested || buildQueued !== null}
            {...(isRunning && turn.isProcessing
              ? { disabledHint: 'Available when the current turn finishes.' }
              : status === 'stopping'
                ? { disabledHint: 'Available once the forge has stopped.' }
                : {})}
          />
          <ForgeArcheSettingsCard
            view={archeSettings.view}
            isLoading={archeSettings.isLoading}
            onEdit={() => setSettingsOpen(true)}
            archeId={forge.archeId}
          />
          <ForgePublishPanel
            forge={forge}
            submission={forgeState.submission}
            onOpenPublish={() => setPublishOpen(true)}
            isPublishRequested={forgeState.isPublishRequested}
          />
          <div className="rounded-lg border p-3">
            <p className="text-sm font-medium">Delete forge</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Removes the workspace and its files, and unpublishes the arche if it is published. The
              arche itself is kept.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete forge
            </Button>
          </div>
        </aside>
      </div>
      <ForgeDeleteDialog
        forge={confirmDelete ? forge : null}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
      <ForgePublishDialog
        forgeId={forge.id}
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onPublish={forgeState.publish}
        canPublish={isRunning && !turn.isProcessing}
        onEditSettings={() => {
          setPublishOpen(false);
          setSettingsOpen(true);
        }}
      />
      <ForgeArcheSettingsDialog
        forgeId={forge.id}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
      <ForgeReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        forgeId={forge.id}
        messages={[...earlier, ...turn.messages]}
        toolEventHistory={turn.toolEventHistory}
        pendingToolEvents={turn.pendingToolEvents}
      />
      <ForgeScreenshotViewer
        screenshots={turn.screenshots}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={() => {
          setViewerIndex(null);
          markScreenshotsSeen();
        }}
      />
    </div>
  );
}
